import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions, Tab, Tabs } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { LocalHospital, Add, UnfoldMore, Save } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { obrasSocialesAPI, centrosAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import { textMatch } from '../../../shared/utils/textUtils';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;
const EMPTY_OS = {
  obraSocial: '', nombre: '', razonSocial: '', direccion: '', cuit: '', fechaFacturacion: '',
  codigoPrestador: '', direccionFacturacion: '', fechaConvenio: '',
  ultimaActualizacionPrecios: '', nroAfiliado: 'N', dias: '', diasVencimientoAutorizacion: '', pideToken: false, observaciones: '',
};

const GrillaObrasSociales = () => {
  const [obrasSociales, setObrasSociales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [page, setPage] = useState(1);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY_OS);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [tabActiva, setTabActiva] = useState('os');

  // Planes
  const [planes, setPlanes] = useState([]);
  const [planNombre, setPlanNombre] = useState('');
  const [editandoPlan, setEditandoPlan] = useState(null);
  const [confirmEliminarPlan, setConfirmEliminarPlan] = useState(null);

  // Adjuntos
  const LABELS_ADJ = ['Credencial', 'Autorización 1', 'Autorización 2', 'Autorización Médica'];
  const [adjuntos, setAdjuntos] = useState(['', '', '', '']);
  const [adjuntosLoading, setAdjuntosLoading] = useState(false);
  const adjuntosBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') + '/AdjuntosObrasSociales/';

  // Sedes (Centros)
  const [centrosDisponibles, setCentrosDisponibles] = useState([]);
  const [centrosSeleccionados, setCentrosSeleccionados] = useState([]);
  const [sedesLoading, setSedesLoading] = useState(false);

  const { sortedData, requestSort, getSortIcon } = useSortableTable(obrasSociales);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await obrasSocialesAPI.getObrasSociales();
      setObrasSociales(res.data || []);
    } catch {
      setToast({ message: 'Error al cargar las obras sociales.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarCentrosDisponibles = useCallback(async () => {
    try {
      const res = await centrosAPI.getCentros();
      setCentrosDisponibles(res.data || []);
    } catch {
      setCentrosDisponibles([]);
    }
  }, []);

  useEffect(() => { cargar(); cargarCentrosDisponibles(); }, [cargar, cargarCentrosDisponibles]);

  const cargarPlanes = useCallback(async (idObraSocial) => {
    try {
      const res = await obrasSocialesAPI.getPlanesPorObraSocial(idObraSocial);
      setPlanes(res.data || []);
    } catch {
      setToast({ message: 'Error al cargar los planes.', type: 'error' });
    }
  }, []);

  const cargarAdjuntos = useCallback(async (idObraSocial) => {
    try {
      const res = await obrasSocialesAPI.getAdjuntosPorObraSocial(idObraSocial);
      const item = res.data?.[0];
      const raw = item?.nombres || item?.Nombres || '';
      const partes = raw.split(';');
      setAdjuntos([partes[0] || '', partes[1] || '', partes[2] || '', partes[3] || '']);
    } catch {
      setAdjuntos(['', '', '', '']);
    }
  }, []);

  const cargarCentrosObraSocial = useCallback(async (idObraSocial) => {
    try {
      const res = await obrasSocialesAPI.getCentrosPorObraSocial(idObraSocial);
      setCentrosSeleccionados((res.data || []).map(c => c.idCentro));
    } catch {
      setCentrosSeleccionados([]);
    }
  }, []);

  const filtradas = sortedData.filter(os =>
    !buscar ||
    textMatch(os.obraSocial, buscar) ||
    textMatch(os.nombre, buscar) ||
    textMatch(os.razonSocial, buscar)
  );
  const totalPages = Math.ceil(filtradas.length / PAGE_SIZE);
  const paginadas = filtradas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleNueva = () => {
    setEditando(null); setForm(EMPTY_OS); setPlanes([]);
    setCentrosSeleccionados([]);
    setTabActiva('os'); setMostrarForm(true);
  };

  const handleEditar = async (os) => {
    setEditando(os);
    setForm({
      obraSocial: os.obraSocial || '', nombre: os.nombre || '',
      razonSocial: os.razonSocial || '',
      direccion: os.direccion || '', cuit: os.cuit || '',
      fechaFacturacion: os.fechaFacturacion || '',
      codigoPrestador: os.codigoPrestador || '',
      direccionFacturacion: os.direccionFacturacion || '',
      fechaConvenio: os.fechaConvenio ? os.fechaConvenio.substring(0, 10) : '',
      ultimaActualizacionPrecios: os.ultimaActualizacionPrecios ? os.ultimaActualizacionPrecios.substring(0, 10) : '',
      nroAfiliado: os.nroAfiliado || 'N',
      dias: os.dias != null ? String(os.dias) : '',
      diasVencimientoAutorizacion: os.diasVencimientoAutorizacion != null ? String(os.diasVencimientoAutorizacion) : '',
      pideToken: !!os.pideToken,
      observaciones: os.observaciones || '',
    });
    setTabActiva('os');
    setMostrarForm(true);
    await cargarPlanes(os.idObraSocial);
    await cargarAdjuntos(os.idObraSocial);
    await cargarCentrosObraSocial(os.idObraSocial);
  };

  const handleCancelar = () => {
    setMostrarForm(false); setEditando(null); setForm(EMPTY_OS);
    setPlanes([]); setPlanNombre(''); setEditandoPlan(null);
    setAdjuntos(['', '', '', '']);
    setCentrosSeleccionados([]);
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        dias: form.dias ? Number(form.dias) : null,
        diasVencimientoAutorizacion: form.diasVencimientoAutorizacion ? Number(form.diasVencimientoAutorizacion) : null,
        pideToken: !!form.pideToken,
        fechaConvenio: form.fechaConvenio || null,
        ultimaActualizacionPrecios: form.ultimaActualizacionPrecios || null,
      };
      if (editando) {
        await obrasSocialesAPI.actualizarObraSocial(editando.idObraSocial, payload);
        setToast({ message: 'Obra social actualizada con éxito.', type: 'success' });
        setMostrarForm(false); setEditando(null); setForm(EMPTY_OS);
      } else {
        const res = await obrasSocialesAPI.guardarObraSocial(payload);
        setEditando({ ...payload, idObraSocial: res.data.idObraSocial });
        setToast({ message: 'Obra social guardada con éxito.', type: 'success' });
      }
      cargar();
    } catch {
      setToast({ message: 'Error al guardar la obra social.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await obrasSocialesAPI.eliminarObraSocial(confirmEliminar);
      setToast({ message: 'Obra social eliminada con éxito.', type: 'success' });
      cargar();
    } catch {
      setToast({ message: 'Error al eliminar la obra social.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  // Planes
  const handleGuardarPlan = async (e) => {
    e.preventDefault();
    if (!editando?.idObraSocial) return;
    try {
      if (editandoPlan) {
        await obrasSocialesAPI.actualizarPlan(editandoPlan.idPlan, { idObraSocial: editando.idObraSocial, plan: planNombre.trim() });
        setToast({ message: 'Plan actualizado con éxito.', type: 'success' });
      } else {
        await obrasSocialesAPI.guardarPlan({ idObraSocial: editando.idObraSocial, plan: planNombre.trim() });
        setToast({ message: 'Plan guardado con éxito.', type: 'success' });
      }
      setPlanNombre(''); setEditandoPlan(null);
      cargarPlanes(editando.idObraSocial);
    } catch {
      setToast({ message: 'Error al guardar el plan.', type: 'error' });
    }
  };

  const confirmarEliminarPlan = async () => {
    try {
      await obrasSocialesAPI.eliminarPlan(confirmEliminarPlan);
      setToast({ message: 'Plan eliminado con éxito.', type: 'success' });
      cargarPlanes(editando.idObraSocial);
    } catch {
      setToast({ message: 'Error al eliminar el plan.', type: 'error' });
    } finally {
      setConfirmEliminarPlan(null);
    }
  };

  const handleSubirAdjunto = async (idx, file) => {
    if (!editando?.idObraSocial || !file) return;
    setAdjuntosLoading(true);
    try {
      const formData = new FormData();
      formData.append(String(idx), file);
      await obrasSocialesAPI.subirAdjuntos(editando.idObraSocial, formData);
      await cargarAdjuntos(editando.idObraSocial);
      setToast({ message: 'Adjunto subido con éxito.', type: 'success' });
    } catch {
      setToast({ message: 'Error al subir el adjunto.', type: 'error' });
    } finally {
      setAdjuntosLoading(false);
    }
  };

  const handleEliminarAdjunto = async (idx) => {
    if (!editando?.idObraSocial) return;
    setAdjuntosLoading(true);
    try {
      await obrasSocialesAPI.eliminarAdjunto(editando.idObraSocial, idx);
      const nuevo = [...adjuntos];
      nuevo[idx] = '';
      setAdjuntos(nuevo);
      setToast({ message: 'Adjunto eliminado.', type: 'success' });
    } catch {
      setToast({ message: 'Error al eliminar el adjunto.', type: 'error' });
    } finally {
      setAdjuntosLoading(false);
    }
  };

  const handleGuardarSedes = async () => {
    if (!editando?.idObraSocial) return;
    setSedesLoading(true);
    try {
      await obrasSocialesAPI.guardarCentrosObraSocial(editando.idObraSocial, centrosSeleccionados);
      setToast({ message: 'Sedes actualizadas con éxito.', type: 'success' });
    } catch {
      setToast({ message: 'Error al guardar las sedes.', type: 'error' });
    } finally {
      setSedesLoading(false);
    }
  };

  const toggleCentro = (idCentro) => {
    setCentrosSeleccionados(prev =>
      prev.includes(idCentro) ? prev.filter(id => id !== idCentro) : [...prev, idCentro]
    );
  };

  const f = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="grilla-turnos">
      <style>{`
        .modal-obras-sociales .modal-content { max-width: none !important; width: 100% !important; }
        .modal-obras-sociales { max-width: 900px !important; width: 90vw !important; }
        .modal-obras-sociales .nav-tabs .nav-link { color: #495057 !important; font-size: 1rem; padding: 0.6rem 1.2rem; }
        .modal-obras-sociales .nav-tabs .nav-link.active { color: #6d1212 !important; font-weight: 700; border-bottom: 3px solid #6d1212; background: none; }
        .modal-obras-sociales .nav-tabs .nav-link:hover { color: #6d1212 !important; }
      `}</style>
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <LocalHospital sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Obras Sociales
          </h2>
          <span className="page-subtitle">Administración de obras sociales</span>
        </div>

        <Modal show={mostrarForm} onHide={handleCancelar} size="xl" dialogClassName="modal-obras-sociales">
          <Modal.Header closeButton>
            <Modal.Title>{editando ? 'Editar Obra Social' : 'Nueva Obra Social'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Tabs activeKey={tabActiva} onSelect={setTabActiva} className="mb-3" style={{ fontSize: '1rem', fontWeight: '600', borderBottom: '2px solid #dee2e6' }}>
              <Tab eventKey="os" title="Obra Social">
                <Form id="form-os" onSubmit={handleGuardar}>

                  <fieldset style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
                    <legend style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6d1212', width: 'auto', padding: '0 0.5rem' }}>Identificación</legend>
                    <div className="row">
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>* Nombre O.S. / Prepaga</Form.Label>
                        <Form.Control type="text" maxLength={100} placeholder="Ej: OSDE, Swiss Medical..." value={form.obraSocial} onChange={f('obraSocial')} required autoFocus />
                      </Form.Group>
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>* Nombre</Form.Label>
                        <Form.Control type="text" maxLength={250} placeholder="Nombre..." value={form.nombre} onChange={f('nombre')} required />
                      </Form.Group>
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>Razón Social (Facturación)</Form.Label>
                        <Form.Control type="text" maxLength={300} placeholder="Nombre legal / fiscal..." value={form.razonSocial} onChange={f('razonSocial')} />
                      </Form.Group>
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>CUIT</Form.Label>
                        <Form.Control type="text" maxLength={15} placeholder="Cuit..." value={form.cuit} onChange={f('cuit')} />
                      </Form.Group>
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>Nro o Código de Prestador</Form.Label>
                        <Form.Control type="text" value={form.codigoPrestador} onChange={f('codigoPrestador')} />
                      </Form.Group>
                    </div>
                  </fieldset>

                  <fieldset style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
                    <legend style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6d1212', width: 'auto', padding: '0 0.5rem' }}>Domicilio</legend>
                    <div className="row">
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>* Dirección</Form.Label>
                        <Form.Control type="text" maxLength={250} placeholder="Ej: Lavalleja 178 CABA..." value={form.direccion} onChange={f('direccion')} required />
                      </Form.Group>
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>Dirección Facturación</Form.Label>
                        <Form.Control type="text" maxLength={250} placeholder="Ej: Lavalleja 178 CABA..." value={form.direccionFacturacion} onChange={f('direccionFacturacion')} />
                      </Form.Group>
                    </div>
                  </fieldset>

                  <fieldset style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
                    <legend style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6d1212', width: 'auto', padding: '0 0.5rem' }}>Facturación y Convenio</legend>
                    <div className="row">
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>Fecha de Facturación</Form.Label>
                        <Form.Control type="text" maxLength={150} placeholder="..." value={form.fechaFacturacion} onChange={f('fechaFacturacion')} />
                      </Form.Group>
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>* Días de cobro</Form.Label>
                        <Form.Control type="number" placeholder="Días..." value={form.dias} onChange={f('dias')} required />
                      </Form.Group>
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>Días venc. autorización</Form.Label>
                        <Form.Control type="number" min={0} placeholder="Días..." value={form.diasVencimientoAutorizacion} onChange={f('diasVencimientoAutorizacion')} />
                      </Form.Group>
                      <Form.Group className="col-md-6 mb-3 d-flex align-items-end">
                        <Form.Check
                          type="checkbox"
                          id="os-pide-token"
                          label="Pide token 24 hs antes del turno"
                          checked={!!form.pideToken}
                          onChange={(e) => setForm(p => ({ ...p, pideToken: e.target.checked }))}
                        />
                      </Form.Group>
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>Fecha de Convenio</Form.Label>
                        <Form.Control type="date" value={form.fechaConvenio} onChange={f('fechaConvenio')} />
                      </Form.Group>
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>Última actualización de precios</Form.Label>
                        <Form.Control type="date" value={form.ultimaActualizacionPrecios} onChange={f('ultimaActualizacionPrecios')} />
                      </Form.Group>
                    </div>
                  </fieldset>

                  <fieldset style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '1rem', marginBottom: '0' }}>
                    <legend style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6d1212', width: 'auto', padding: '0 0.5rem' }}>Otros</legend>
                    <div className="row">
                      <Form.Group className="col-md-6 mb-3">
                        <Form.Label>Número de Afiliado</Form.Label>
                        <div className="d-flex gap-3 mt-1">
                          <Form.Check type="radio" label="Sí" value="S" checked={form.nroAfiliado === 'S'} onChange={f('nroAfiliado')} />
                          <Form.Check type="radio" label="No" value="N" checked={form.nroAfiliado === 'N'} onChange={f('nroAfiliado')} />
                        </div>
                      </Form.Group>
                      <Form.Group className="col-md-12 mb-0">
                        <Form.Label>Observaciones</Form.Label>
                        <Form.Control type="text" maxLength={500} placeholder="Observaciones..." value={form.observaciones} onChange={f('observaciones')} />
                      </Form.Group>
                    </div>
                  </fieldset>

                </Form>
              </Tab>
              <Tab eventKey="planes" title="Planes" disabled={!editando}>
                <Form onSubmit={handleGuardarPlan} className="d-flex gap-2 mb-3">
                  <Form.Control type="text" placeholder="Plan..." value={planNombre}
                    onChange={(e) => setPlanNombre(e.target.value)} required maxLength={500} />
                  <Button type="submit" variant="success" disabled={!planNombre.trim()}>
                    <Save sx={{ fontSize: 16 }} /> {editandoPlan ? 'Actualizar' : 'Agregar'}
                  </Button>
                  {editandoPlan && (
                    <Button variant="secondary" onClick={() => { setEditandoPlan(null); setPlanNombre(''); }}>Cancelar</Button>
                  )}
                </Form>
                <DataTable style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '0.5rem', borderBottom: '2px solid #dee2e6', textAlign: 'left', width: '70%' }}>Plan</th>
                      <th style={{ padding: '0.5rem', borderBottom: '2px solid #dee2e6', textAlign: 'left', width: '30%' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planes.length === 0 ? (
                      <tr><td colSpan="2" style={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>No hay planes asociados</td></tr>
                    ) : planes.map(p => (
                      <tr key={p.idPlan} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '0.5rem' }}>{p.plans}</td>
                        <td style={{ padding: '0.5rem' }}>
                          <RowActions onEdit={() => { setEditandoPlan(p); setPlanNombre(p.plans); }} onDelete={() => setConfirmEliminarPlan(p.idPlan)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </Tab>
              <Tab eventKey="adjuntos" title="Adjuntos" disabled={!editando}>
                {adjuntosLoading && <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Procesando...</p>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {LABELS_ADJ.map((label, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                      <span style={{ width: '140px', fontWeight: '600', fontSize: '0.875rem', flexShrink: 0 }}>{label}</span>
                      {adjuntos[idx] ? (
                        <>
                          <a href={`${adjuntosBaseUrl}${adjuntos[idx]}`} target="_blank" rel="noreferrer"
                            style={{ fontSize: '0.875rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {adjuntos[idx]}
                          </a>
                          <Button variant="outline-danger" size="sm" onClick={() => handleEliminarAdjunto(idx)} disabled={adjuntosLoading}>Eliminar</Button>
                        </>
                      ) : (
                        <Form.Control
                          type="file"
                          accept="image/*,.pdf"
                          size="sm"
                          style={{ flex: 1 }}
                          disabled={adjuntosLoading}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleSubirAdjunto(idx, file);
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </Tab>
              <Tab eventKey="sedes" title="Sedes" disabled={!editando}>
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', color: '#64748b', margin: '0 0 0.75rem' }}>
                    Seleccioná las sedes en las que trabaja esta obra social:
                  </p>
                  {centrosDisponibles.length === 0 ? (
                    <p style={{ color: '#64748b', fontSize: '0.875rem' }}>No hay centros disponibles.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {centrosDisponibles.map(c => (
                        <label key={c.idCentro} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', border: '1px solid #dee2e6', borderRadius: '6px', cursor: 'pointer', background: centrosSeleccionados.includes(c.idCentro) ? '#f0fdf4' : 'transparent' }}>
                          <input
                            type="checkbox"
                            checked={centrosSeleccionados.includes(c.idCentro)}
                            onChange={() => toggleCentro(c.idCentro)}
                            style={{ width: '1.1rem', height: '1.1rem' }}
                          />
                          <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{c.centro}</span>
                          {c.direccion && <span style={{ fontSize: '0.8rem', color: '#64748b' }}>— {c.direccion}</span>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <Button variant="success" onClick={handleGuardarSedes} disabled={sedesLoading || !editando}>
                  <Save sx={{ fontSize: 16, mr: 0.5 }} /> Guardar Sedes
                </Button>
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCancelar} disabled={loading}>Cancelar</Button>
            {tabActiva === 'os' && (
              <Button type="submit" form="form-os" variant="success" disabled={loading}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {editando ? 'Actualizar' : 'Guardar'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Filtros de Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${buscar ? 'filter-active' : ''}`}>
                <label className="filter-label">Obra Social</label>
                <input type="text" className="filter-input" placeholder="Buscar obra social..."
                  value={buscar} onChange={(e) => { setBuscar(e.target.value); setPage(1); }} />
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={() => setPage(1)} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => { setBuscar(''); setPage(1); }}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{filtradas.length} obras sociales encontradas</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={handleNueva} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nueva Obra Social
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idObraSocial')} style={{ cursor: 'pointer' }}>
                    # {getSortIcon('idObraSocial') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('obraSocial')} style={{ cursor: 'pointer' }}>
                    O.S. / Prepaga {getSortIcon('obraSocial') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('nombre')} style={{ cursor: 'pointer' }}>
                    Nombre {getSortIcon('nombre') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('razonSocial')} style={{ cursor: 'pointer' }}>
                    Razón Social {getSortIcon('razonSocial') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('direccion')} style={{ cursor: 'pointer' }}>
                    Dirección {getSortIcon('direccion') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('cuit')} style={{ cursor: 'pointer' }}>
                    CUIT {getSortIcon('cuit') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('codigoPrestador')} style={{ cursor: 'pointer' }}>
                    Cód. Prestador {getSortIcon('codigoPrestador') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('dias')} style={{ cursor: 'pointer' }}>
                    Días Cobro {getSortIcon('dias') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('diasVencimientoAutorizacion')} style={{ cursor: 'pointer' }}>
                    Días Venc. Aut. {getSortIcon('diasVencimientoAutorizacion') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th className="action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginadas.length === 0 ? (
                  <tr><td colSpan="10" className="empty-state">{loading ? 'Cargando...' : 'No hay obras sociales para mostrar'}</td></tr>
                ) : (
                  paginadas.map((os) => (
                    <tr key={os.idObraSocial}>
                      <td>{os.idObraSocial}</td>
                      <td>{os.obraSocial}</td>
                      <td>{os.nombre}</td>
                      <td>{os.razonSocial || '-'}</td>
                      <td>{os.direccion}</td>
                      <td>{os.cuit}</td>
                      <td>{os.codigoPrestador}</td>
                      <td>{os.dias}</td>
                      <td>{os.diasVencimientoAutorizacion}</td>
                      <td className="action-cell">
                        <RowActions onEdit={() => handleEditar(os)} onDelete={() => setConfirmEliminar(os.idObraSocial)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar obra social"
        message="¿Estás seguro que querés eliminar esta obra social?"
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      <ConfirmModal
        show={confirmEliminarPlan !== null}
        title="Eliminar plan"
        message="¿Estás seguro que querés eliminar este plan?"
        onConfirm={confirmarEliminarPlan}
        onCancel={() => setConfirmEliminarPlan(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaObrasSociales;
